import type { ScheduledFilterValue } from "../types";

const SCHEDULED_VALUES: readonly ScheduledFilterValue[] = [
  "overdue",
  "unscheduled",
  "scheduled",
];

export interface ScheduledMatchTask {
  scheduled_date: string | null;
  completed: boolean;
  persistent: boolean;
}

/** `?scheduled=overdue` stays valid. Several choices use a comma, same as priority. */
export function parseScheduledFilterParam(
  param: string | undefined,
): ScheduledFilterValue[] {
  if (!param) return [];
  const seen = new Set<ScheduledFilterValue>();
  const out: ScheduledFilterValue[] = [];
  for (const part of param.split(",")) {
    const value = part.trim();
    if (
      (SCHEDULED_VALUES as readonly string[]).includes(value) &&
      !seen.has(value as ScheduledFilterValue)
    ) {
      const scheduled = value as ScheduledFilterValue;
      seen.add(scheduled);
      out.push(scheduled);
    }
  }
  return out;
}

export function serializeScheduledFilters(
  values: readonly ScheduledFilterValue[],
): string | undefined {
  return values.length > 0 ? values.join(",") : undefined;
}

/**
 * One choice steps Overdue → Unscheduled → Scheduled → off.
 * Several choices turn the schedule filter off, same as the tag key.
 */
export function cycleScheduledFilters(
  current: readonly ScheduledFilterValue[],
): ScheduledFilterValue[] {
  if (current.length !== 1) {
    return current.length === 0 ? ["overdue"] : [];
  }
  if (current[0] === "overdue") return ["unscheduled"];
  if (current[0] === "unscheduled") return ["scheduled"];
  return [];
}

/**
 * A task matches when it fits any chosen schedule state.
 * Carry over tasks roll forward on their own, so they are not overdue
 * or unscheduled.
 */
export function taskMatchesScheduledFilters(
  task: ScheduledMatchTask,
  filters: readonly ScheduledFilterValue[],
  today: string,
): boolean {
  if (filters.length === 0) return true;
  return filters.some((sf) => {
    if (sf === "overdue") {
      return (
        !!task.scheduled_date &&
        task.scheduled_date < today &&
        !task.completed &&
        !task.persistent
      );
    }
    if (sf === "unscheduled") {
      return task.scheduled_date === null && !task.persistent;
    }
    return task.scheduled_date !== null;
  });
}

/**
 * The calendar side list only holds unscheduled and overdue tasks.
 * Choosing Scheduled alone still matches none of them.
 */
export function taskMatchesPanelSchedule(
  task: { scheduled_date: string | null },
  filters: readonly ScheduledFilterValue[],
  today: string,
): boolean {
  if (filters.length === 0) return true;
  return filters.some((sf) => {
    if (sf === "overdue") {
      return !!task.scheduled_date && task.scheduled_date < today;
    }
    if (sf === "unscheduled") return task.scheduled_date === null;
    return false;
  });
}
